import React from 'react';
import Head from 'next/head';

// AdditionalTag uses `name` only (no `property` field) because the AAPI SEOMetaData.Meta
// contract only exposes name() and content(). If the contract adds a `property` field in
// the future, extend this type and the backend SeoTagEntry POJO accordingly.
type MetaTag = { type: 'meta'; name: string; content: string };
type LinkTag = { type: 'link'; rel: string; href: string };
export type AdditionalTag = MetaTag | LinkTag;

export type SeoMetaTagProps = {
  pageTitle?: string;
  productTitle?: string;
  description?: string;
  imageUrl?: string;
  url?: string;
  additionalTags?: AdditionalTag[];
};

export const SeoMetaTag = (props: SeoMetaTagProps) => {
  const { pageTitle, productTitle, description, imageUrl, url, additionalTags = [] } = props;

  // Next.js <Head> iterates direct children for tag deduplication. React Fragments
  // (<>...</>) break this traversal, so each tag must be a direct child of <Head>.
  return (
    <Head>
      {/* Primary */}
      {pageTitle && <title>{pageTitle}</title>}
      {productTitle && <meta name="title" content={productTitle} />}
      {description && <meta name="description" content={description} />}
      {url && <link rel="canonical" href={url} />}

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      {productTitle && <meta property="og:title" content={productTitle} />}
      {description && <meta property="og:description" content={description} />}
      {imageUrl && <meta property="og:image" content={imageUrl} />}
      {url && <meta property="og:url" content={url} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary" />
      {productTitle && <meta name="twitter:title" content={productTitle} />}
      {description && <meta name="twitter:description" content={description} />}
      {imageUrl && <meta name="twitter:image" content={imageUrl} />}
      {url && <meta name="twitter:url" content={url} />}

      {/* Additional SEO tags from hydrator */}
      {additionalTags.map((tag, index) =>
        tag.type === 'meta' ? (
          <meta key={`meta-${tag.name}-${index}`} name={tag.name} content={tag.content} />
        ) : (
          <link key={`link-${tag.rel}-${index}`} rel={tag.rel} href={tag.href} />
        )
      )}
    </Head>
  );
};
